import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { auth } from './Firebase';
import CustomSelect from './CustomSelect';
import { Helmet } from 'react-helmet-async';
import { onAuthStateChanged } from 'firebase/auth';
import { useCompetitions } from './CompetitionContext'; // Dodajemy kontekst
import RequireVip from './RequireVip';

import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const YourSimulation = () => {
  const { id: competitionId } = useParams();
  const navigate = useNavigate();
  const { competitions, loading: competitionsLoading } = useCompetitions();

  const [selectedComp, setSelectedComp] = useState(null);

  const { register, control, handleSubmit, watch, setValue } = useForm({
    defaultValues: { conditions: [{ type: 'specific_match' }] },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'conditions' });
  const [leagueData, setLeagueData] = useState(null);
  const [simulationLink, setSimulationLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [selectedTeamIds, setSelectedTeamIds] = useState({});
  const { updateCompetition, refreshCompetitions } = useCompetitions(); // Dodajemy refreshCompetitions
  
  const conditionOptions = [
    { value: 'specific_match', label: 'Wynik meczu' },
    { value: 'all_wins', label: 'Same zwycięstwa' },
    { value: 'all_losses', label: 'Same porażki' },
    { value: 'elo_change', label: 'Zmiana Elo drużyny' },
  ];

  // Pobierz userId z Firebase
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });
    return unsubscribe;
  }, []);
  
  useEffect(() => {
    if (competitionsLoading || !competitionId) return;

    const comp = Object.values(competitions)
      .flat()
      .find(c => c.competition_id === parseInt(competitionId));

    if (comp) {
      setSelectedComp(comp);
    }
  }, [competitions, competitionsLoading, competitionId]);

  // Pobierz dane ligi z endpointu /api/league/[id]
  useEffect(() => {
    const fetchLeagueData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/league/431`);
        setLeagueData(response.data);
      } catch (error) {
        console.error('Błąd pobierania danych ligi:', error);
      }
    };
    fetchLeagueData();
  }, [competitionId]);

  // Filtruj nierozgrane mecze i ekstraktuj drużyny
  const unplayedMatches = leagueData?.matches
    ? leagueData.matches.filter((match) => match.status !== 'ended')
    : [];
  const teams = leagueData?.teams ? leagueData.teams : [];

  // Obsługa submitu formularza
  const onSubmit = async (data) => {
    if (!userId) {
      alert('Musisz być zalogowany, aby utworzyć symulację.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        competitionId: 431,
        iterations: 10000,
        userId,
        conditions: data.conditions.map((condition) => ({
          type: condition.type,
          matchId: condition.type === 'specific_match' ? parseInt(condition.matchId) : 0,
          homeGoals: condition.type === 'specific_match' ? parseInt(condition.homeGoals) : 0,
          awayGoals: condition.type === 'specific_match' ? parseInt(condition.awayGoals) : 0,
          teamId: (condition.type === 'all_wins' || condition.type === 'all_losses' || condition.type === 'elo_change' || condition.type === 'points_change') ? parseInt(condition.teamId) : 0,
          newElo: condition.type === 'elo_change' ? parseInt(condition.newElo) : 0,
		  pointsDelta: condition.type === 'points_change' ? parseInt(condition.pointsDelta) : 0

        })),
      };
      console.log('Payload:', payload);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/simulation/create`, payload);
      const simulationId = response.data.simulationId;
      setSimulationLink(`symuliga.pl/yourSimulation/${simulationId}`);

      // Aktualizacja kontekstu z nową symulacją
      if (updateCompetition) {
        updateCompetition(parseInt(competitionId), simulationId); // Wywołanie aktualizacji kontekstu
      }
      // Wymuś odświeżenie danych z serwera
      if (refreshCompetitions) {
        await refreshCompetitions(); // Poczekaj na odświeżenie
      }
    } catch (error) {
      console.error('Błąd podczas uruchamiania symulacji:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Watch dla dynamicznego renderowania pól w zależności od typu
  const watchedConditions = watch('conditions');

  // Aktualizacja po zmianie teamId lub newElo
  useEffect(() => {
    const newSelectedTeamIds = {};
    fields.forEach((field, index) => {
      const teamId = watchedConditions[index]?.teamId;
      const newEloFromForm = watchedConditions[index]?.newElo;
      if (newEloFromForm !== undefined && newEloFromForm !== '') {
        newSelectedTeamIds[index] = parseInt(newEloFromForm) || 1400;
      } else if (teamId) {
        const team = teams.find(t => t.teamId === teamId);
        const defaultElo = team ? team.elo : 1400;
        newSelectedTeamIds[index] = defaultElo;
        if (!watchedConditions[index]?.newElo) {
          setValue(`conditions.${index}.newElo`, defaultElo, { shouldValidate: true });
        }
      } else {
        newSelectedTeamIds[index] = 1400;
      }
    });
    setSelectedTeamIds(newSelectedTeamIds);
  }, [watchedConditions, teams, fields, setValue]);

  // Przekierowanie po zakończeniu symulacji
  useEffect(() => {
    if (simulationLink) {
      navigate(`/liga/431/mistrzostwa-swiata`);
    }
  }, [simulationLink, competitionId, navigate]);

  // Funkcja do obsługi zmiany teamId
  const handleTeamChange = (index, event) => {
    const teamId = event.target.value;
    if (teamId) {
      const team = teams.find(t => t.teamId === teamId);
      const defaultElo = team ? team.elo : 1400;
      setValue(`conditions.${index}.newElo`, defaultElo, { shouldValidate: true });
      setSelectedTeamIds(prev => ({ ...prev, [index]: defaultElo }));
    }
  };
  
  if (competitionsLoading || !leagueData) {
    return (
      <Box sx={{ textAlign: 'center', padding: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Ładowanie…</Typography>
      </Box>
    );
  }
  
  const leagueName = 'Mistrzostwa Świata';
  const pageTitle = `Kalkulator ligowy - ${leagueName} | SymuLiga`;
  const pageDesc = `Stwórz własny scenariusz dla Mistrzostw Świata. Edytuj wyniki pozostałych meczów i sprawdź, jak zmienią się szanse przy użyciu symulacji Monte Carlo.`;

  return (
  <>
	<Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={`Kalkulator - Mistrzostwa Świata | SymuLiga`} />
        <meta property="og:description" content="Interaktywny kalkulator tabeli końcowej. Wprowadź wyniki oraz własne warunki i zobacz matematyczną prognozę." />
    </Helmet>
    <Box
      sx={{
        padding: { xs: '0px', md: '0px' },
        maxWidth: '1200px',
        margin: '0 auto',
      }}
    >
      <Typography variant="h4" component="h1" sx={{ mb: 2, textAlign: 'center', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
        Symulator (własny scenariusz) - {leagueName}
      </Typography>
	  <Typography variant="subtitle1" component="h2" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
		  Wprowadź własne wyniki oraz dodatkowe warunki i oblicz własną symulację warunkową (10 000 iteracji)
	  </Typography>

      {!leagueData ? (
        <Box sx={{ textAlign: 'center', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
          <Typography variant="h6">Ładowanie danych...</Typography>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <TableContainer
            component={Paper}
            sx={{
              width: '100%',
              margin: '0 auto',
              overflowX: 'auto',
              borderRadius: '12px',
              mb: 3,
            }}
          >
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ padding: '8px', fontSize: '0.9rem', textAlign: 'center' }}>
                    Warunek
                  </TableCell>
                  <TableCell sx={{ padding: '8px', fontSize: '0.9rem', textAlign: 'center' }}>
                    Mecz / Drużyna
                  </TableCell>
                  <TableCell sx={{ padding: '8px', fontSize: '0.9rem', textAlign: 'center' }}>
                    Gole gospodarza
                  </TableCell>
                  <TableCell sx={{ padding: '8px', fontSize: '0.9rem', textAlign: 'center' }}>
                    Gole gościa
                  </TableCell>
                  <TableCell sx={{ padding: '8px', fontSize: '0.9rem', textAlign: 'center' }}>
                    Nowe Elo
                  </TableCell>
                  <TableCell sx={{ padding: '8px', fontSize: '0.9rem', textAlign: 'center' }}>
                    Akcje
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, index) => {
                  const conditionType = watchedConditions[index]?.type || 'specific_match';

                  return (
                    <TableRow key={field.id}>
                      <TableCell sx={{ padding: '8px', textAlign: 'center' }}>
						  <FormControl sx={{ minWidth: 100 }}>
							<InputLabel id={`condition-type-label-${index}`}>Warunek</InputLabel>
							<Select
							  labelId={`condition-type-label-${index}`}
							  label="Warunek"
							  {...register(`conditions.${index}.type`)}
							  defaultValue={field.type || 'specific_match'}
							>
							  <MenuItem value="specific_match">Wynik meczu</MenuItem>
							  <MenuItem value="all_wins">Same zwycięstwa</MenuItem>
							  <MenuItem value="all_losses">Same porażki</MenuItem>
							  <MenuItem value="elo_change">Zmiana ELO drużyny</MenuItem>
							</Select>
						  </FormControl>
						</TableCell>

						<TableCell sx={{ padding: '8px', textAlign: 'center' }}>
						  {conditionType === 'specific_match' ? (
							<Autocomplete
							  options={leagueData?.matches || []}
							  size="small"
							  // Łączymy style: minimalna szerokość 150px oraz wyśrodkowanie tekstu z oryginalnego designu
							  sx={{ 
								minWidth: 250
							
							  }}
							  value={leagueData?.matches?.find(m => m.matchId === parseInt(watch(`conditions.${index}.matchId`))) || null}
							  
							  // Dane z nowego widoku: formatowanie tekstu z uwzględnieniem statusów planned/ended
							  getOptionLabel={(option) => 
								`[${option.round}] ${option.homeTeamName} - ${option.awayTeamName} ${
								  option.status === 'ended' 
									? `(${option.homeGoals}:${option.awayGoals})` 
									: '?:?'
								}`
							  }
							  
							  filterOptions={(options, state) => {
								const search = state.inputValue.toLowerCase();
								return options.filter(
								  (o) =>
									o.homeTeamName.toLowerCase().includes(search) ||
									o.awayTeamName.toLowerCase().includes(search) ||
									o.round.toLowerCase().includes(search)
								);
							  }}
							  
							  onChange={(event, newValue) => {
								setValue(`conditions.${index}.matchId`, newValue ? newValue.matchId : '');
							  }}
							  
							  // Kluczowe dopasowanie wyglądu: TextField udaje stary InputLabel i Select pod kątem stylówki MUI
							  renderInput={(params) => (
								<TextField
								  {...params}
								  label="Mecz" // Taki sam label jak w starym designie
								  variant="outlined"
								  required
								  
								/>
							  )}
							/>
						  ) : conditionType === 'points_change' ? (
							/* NOWY BLOK: Wybór drużyny + Wartość punktów obok siebie */
							<Box sx={{ display: 'flex', gap: 1, minWidth: 280 }}>
							  <FormControl sx={{ flexGrow: 2 }}>
								<InputLabel id={`team-select-label-${index}`}>Drużyna</InputLabel>
								<Select
								  labelId={`team-select-label-${index}`}
								  label="Drużyna"
								  {...register(`conditions.${index}.teamId`)}
								  defaultValue=""
								  required
								>
								  <MenuItem value="">Wybierz drużynę</MenuItem>
								  {teams.map((team) => (
									<MenuItem key={team.teamId} value={team.teamId}>{team.teamName}</MenuItem>
								  ))}
								</Select>
							  </FormControl>

							  <TextField
								label="Punkty"
								type="number"
								placeholder="np. 5 lub -3"
								sx={{ flexGrow: 1, width: '90px' }}
								{...register(`conditions.${index}.pointsDelta`, { valueAsNumber: true })}
								required
							  />
							</Box>
						  ) : (
							<FormControl sx={{ minWidth: 150 }}>
							  <InputLabel id={`team-select-label-${index}`}>Drużyna</InputLabel>
							  <Select
								labelId={`team-select-label-${index}`}
								label="Drużyna"
								{...register(`conditions.${index}.teamId`)}
								onChange={(e) => handleTeamChange(index, e)}
							  >
								<MenuItem value="">Wybierz drużynę</MenuItem>
								{teams.map((team) => (
								  <MenuItem key={team.teamId} value={team.teamId}>
									{team.teamName}
								  </MenuItem>
								))}
							  </Select>
							</FormControl>
						  )}
						</TableCell>

                      <TableCell sx={{ padding: '4px', textAlign: 'center' }}>
                        {conditionType === 'specific_match' ? (
                          <TextField
                            label="Gole gospodarza"
                            type="number"
                            inputProps={{ min: 0 }}
                            {...register(`conditions.${index}.homeGoals`, { min: 0 })}
                            sx={{ width: '50px' }}
                            InputLabelProps={{ shrink: true }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>

                      <TableCell sx={{ padding: '4px', textAlign: 'center' }}>
                        {conditionType === 'specific_match' ? (
                          <TextField
                            label="Gole gościa"
                            type="number"
                            inputProps={{ min: 0 }}
                            {...register(`conditions.${index}.awayGoals`, { min: 0 })}
                            sx={{ width: '50px' }}
                            InputLabelProps={{ shrink: true }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>

                      <TableCell sx={{ padding: '8px', textAlign: 'center' }}>
                        {conditionType === 'elo_change' ? (
                          <TextField
                            label="Nowe Elo"
                            type="number"
                            inputProps={{ min: 0 }}
                            {...register(`conditions.${index}.newElo`, {
                              min: 0,
                              onChange: (e) => {
                                const newElo = parseInt(e.target.value) || 0;
                                setSelectedTeamIds(prev => ({ ...prev, [index]: newElo }));
                              },
                            })}
                            sx={{ width: '60px' }}
                            InputLabelProps={{ shrink: true }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>

                      <TableCell sx={{ padding: '8px', textAlign: 'center' }}>
                        <IconButton
                          onClick={() => remove(index)}
                          disabled={fields.length <= 1}
                          sx={{
                            color: 'error.main',
                            '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => append({ type: 'specific_match' })}
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': { borderColor: 'primary.dark', backgroundColor: 'primary.light' },
              }}
            >
              Dodaj kolejny warunek
            </Button>

            <Button
              variant="contained"
              type="submit"
              disabled={loading}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.dark' },
                '&:disabled': { backgroundColor: 'action.disabledBackground' },
              }}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Symulacja w toku...' : 'Uruchom symulację'}
            </Button>
          </Box>
          <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
            Po wykonaniu obliczeń zostaniesz przeniesiony na stronę ligi. Symulacja będzie widoczna w konkretnych podstronach na liście symulacji jako najnowsza + oznaczona gwiazdką.
          </Typography>
        </Box>
      )}
    </Box>
   </>

  );
};

export default YourSimulation;